import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { fetchFinanceData } from '../../../services/owner/financeApi';
import { peekOwnerCache } from '../../../services/cache/ownerCache';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';
import {
  exportFinanceCsv,
  exportFinanceExcel,
  exportFinancePdf,
  exportFinance,
  printFinanceList,
} from './exportFinance';
import '../ownerPages.css';
import './finance.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const NEON_GREEN = '#94FF00';
const TEXT_SECONDARY = '#94a3b8';
const GRID_COLOR = 'rgba(148, 163, 184, 0.08)';

function PaymentBadge({ status }) {
  const map = {
    paid: 'owner-badge-success',
    active: 'owner-badge-success',
    pending: 'owner-badge-info',
    trial: 'owner-badge-info',
    overdue: 'owner-badge-warning',
    unpaid: 'owner-badge-warning',
    completed: 'owner-badge-success',
    failed: 'owner-badge-warning',
  };
  const label = status === 'trial' ? 'Free Trial' : (status.charAt(0).toUpperCase() + status.slice(1));
  return (
    <span className={`owner-badge ${map[status] || 'owner-badge-info'}`}>
      {label}
    </span>
  );
}

function FinancialManagement() {
  const { formatPrice, formatCompact } = usePlatformSettings();
  const cached = peekOwnerCache('finance');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [subscriptionPayments, setSubscriptionPayments] = useState(cached?.subscriptionPayments || []);
  const [revenueSummary, setRevenueSummary] = useState(cached?.revenueSummary || {
    monthly: 0,
    annual: 0,
    outstanding: 0,
    collected: 0,
  });
  const [revenueChartData, setRevenueChartData] = useState(cached?.revenueChartData || { labels: [], values: [] });

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1A1F2B',
        borderColor: 'rgba(148, 163, 184, 0.14)',
        borderWidth: 1,
        titleColor: '#f1f5f9',
        bodyColor: TEXT_SECONDARY,
      },
    },
    scales: {
      x: {
        ticks: { color: TEXT_SECONDARY },
        grid: { color: GRID_COLOR },
      },
      y: {
        ticks: {
          color: TEXT_SECONDARY,
          callback: (value) => formatCompact(value),
        },
        grid: { color: GRID_COLOR },
      },
    },
  }), [formatCompact]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!peekOwnerCache('finance')) setLoading(true);
        setError('');
        const data = await fetchFinanceData();
        if (cancelled) return;
        setSubscriptionPayments(data.subscriptionPayments);
        setRevenueSummary(data.revenueSummary);
        setRevenueChartData(data.revenueChartData);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load finance data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const chartData = {
    labels: revenueChartData.labels,
    datasets: [
      {
        label: 'Revenue',
        data: revenueChartData.values,
        backgroundColor: 'rgba(148, 255, 0, 0.7)',
        borderColor: NEON_GREEN,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const filterRow = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (row) =>
      !query ||
      Object.values(row).some((value) => String(value).toLowerCase().includes(query));
  }, [search]);

  const filteredSubscriptionPayments = subscriptionPayments.filter(filterRow);

  return (
    <div>
      <PageHeader
        title="Financial Management"
        subtitle="Track subscription payments and revenue"
      />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading finance data...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-member-toolbar admin-list-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search payments, gyms, invoices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printFinanceList(filteredSubscriptionPayments)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportFinanceCsv(filteredSubscriptionPayments)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportFinancePdf(filteredSubscriptionPayments)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportFinanceExcel(filteredSubscriptionPayments)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportFinance(filteredSubscriptionPayments)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section finance-section">
        <h2 className="owner-card-title">Subscription Payments</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Gym</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Billing Cycle</th>
                <th>Paid On</th>
                <th>Method</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptionPayments.length > 0 ? (
                filteredSubscriptionPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.id}</td>
                    <td>{payment.gym}</td>
                    <td>{payment.plan}</td>
                    <td className="finance-amount-positive">
                      {payment.isTrial || payment.status === 'trial'
                        ? '—'
                        : formatPrice(payment.amount)}
                    </td>
                    <td>{payment.billingCycle}</td>
                    <td>{payment.paidOn}</td>
                    <td>{payment.method}</td>
                    <td><PaymentBadge status={payment.status} /></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No payments found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section">
        <h2 className="owner-card-title">Revenue</h2>
        <div className="finance-revenue-grid">
          <div className="finance-revenue-item">
            <div className="finance-revenue-item-label">Monthly Revenue</div>
            <div className="finance-revenue-item-value">{formatPrice(revenueSummary.monthly)}</div>
          </div>
          <div className="finance-revenue-item">
            <div className="finance-revenue-item-label">Annual Revenue</div>
            <div className="finance-revenue-item-value">{formatPrice(revenueSummary.annual)}</div>
          </div>
          <div className="finance-revenue-item">
            <div className="finance-revenue-item-label">Collected (MTD)</div>
            <div className="finance-revenue-item-value">{formatPrice(revenueSummary.collected)}</div>
          </div>
          <div className="finance-revenue-item">
            <div className="finance-revenue-item-label">Outstanding</div>
            <div className="finance-revenue-item-value finance-revenue-item-value-warning">
              {formatPrice(revenueSummary.outstanding)}
            </div>
          </div>
        </div>
        <div className="finance-chart-container">
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}

export default FinancialManagement;
